import { Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import Button from '../Button';
import {
  getMondayOfWeek,
  getSundayOfWeek,
  formatDate,
  getMonthWeeks,
  isMonday,
} from '../../utils/DateUtils';
import { twMerge } from 'tailwind-merge';
import API from '../../services/api';
import * as dateFns from 'date-fns';
import { useGlobalContext } from '../context/GlobalProvider';

export default function TimeTrackingProjectItem({
  project,
  currentDate,
  monthlyHours,
  employeeId,
  onCallback,
}) {
  const { setPendingChanges: setGlobalPendingChanges } = useGlobalContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [pendingChanges, setPendingChanges] = useState({});

  // Handle hour input change
  const handleHourChange = (week, oldValue, value) => {
    const parsedOldValue = parseFloat(oldValue || 0);
    const parsedHours = parseFloat(value);
    if (parsedOldValue !== parsedHours) {
      if (parsedHours < 0 || parsedHours > 168) return;
      // Get Monday and Sunday dates using our utility functions
      const monday = getMondayOfWeek(new Date(week.startDate));
      const sunday = getSundayOfWeek(monday);
      setPendingChanges((prev) => ({
        ...prev,
        [week.key]: {
          weekStartDate: formatDate(monday),
          weekEndDate: formatDate(sunday),
          weekNumber: dateFns.getWeek(monday),
          hours: parsedHours,
        },
      }));
    } else {
      //remove the key from the pending changes
      setPendingChanges((prev) => {
        const { [week.key]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSaveAll = async () => {
    setError('');
    const projectChanges = Object.values(pendingChanges);

    if (projectChanges.length === 0) return;

    // Validate dates before proceeding
    const invalidChanges = projectChanges.filter((change) => {
      const startDate = new Date(change.weekStartDate + 'T00:00:00');
      const endDate = new Date(change.weekEndDate + 'T00:00:00');

      // Check if start date is a Monday
      if (!isMonday(startDate)) {
        console.error('Invalid start date - not a Monday', {
          date: change.weekStartDate,
          day: startDate.getDay(),
        });
        return true;
      }

      // Check if end date is 6 days after start date
      const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);
      if (Math.round(daysDiff) !== 6) {
        console.error('Invalid date range', {
          start: change.weekStartDate,
          end: change.weekEndDate,
          diff: daysDiff,
        });
        return true;
      }

      return false;
    });

    if (invalidChanges.length > 0) {
      setError('Invalid week dates detected. Please refresh and try again.');
      return;
    }

    try {
      setIsLoading(true);
      for (const change of projectChanges) {
        const payload = {
          projectId: parseInt(project.project_id),
          employeeId: parseInt(employeeId),
          hours: change.hours,
          week_nr: change.weekNumber,
          startDate: change.weekStartDate,
          endDate: change.weekEndDate,
        };

        console.log('Saving hours with payload:', payload);
        await API.saveHours(payload);
      }

      await onCallback();
      setPendingChanges({});
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message || 'Failed to save hours');
    } finally {
      setIsLoading(false);
    }
  };

  // Get status color based on project status
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'in progress':
        return 'text-green-500';
      case 'delayed':
        return 'text-red-500';
      case 'not started':
        return 'text-gray-500';
      default:
        return 'text-gray-700';
    }
  };

  const listWeeks = getMonthWeeks(currentDate);

  useEffect(() => {
    setPendingChanges({});
  }, [project, employeeId]);

  const isPendingChanges =
    Object.values(pendingChanges).filter(Boolean).length > 0;

  const isValidPendingChanges = Object.values(pendingChanges).every(
    (change) => !isNaN(change.hours),
  );

  useEffect(() => {
    setGlobalPendingChanges((prev) => ({
      ...prev,
      [project.project_id]: isPendingChanges,
    }));
  }, [isPendingChanges, project.project_id, setGlobalPendingChanges]);

  return (
    <div className="p-6 flex flex-col gap-4 rounded-lg border border-gray-200">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-xl font-bold">{project.company_name}</h3>
          </div>
          <div className="text-gray-600">
            {project.sow_description || project.project_name} (
            {project.project_code})
          </div>
        </div>
        <div className="text-right min-w-48">
          <div className="flex items-center gap-2 justify-end mb-1">
            <span className="font-medium">Status:</span>
            <span className={getStatusColor(project.deliverable_status)}>
              {project.deliverable_status}
            </span>
          </div>
          <div className="text-gray-600">
            Due:{' '}
            {project.anticipated_end_date
              ? formatDate(new Date(project.anticipated_end_date))
              : 'Not set'}
          </div>
        </div>
      </div>

      <div
        className={twMerge(
          'grid grid-cols-4 gap-4 mt-4',
          listWeeks.length > 4 ? 'grid-cols-5' : 'grid-cols-4',
        )}
      >
        {listWeeks.map((week) => {
          const hourEntry = monthlyHours[week.key];
          const pendingEntry = pendingChanges[week.key];

          const displayValue =
            pendingEntry !== undefined
              ? pendingEntry.hours
              : hourEntry?.hours_worked;

          return (
            <div
              key={week.key}
              className={`text-center p-4 rounded-lg ${
                week.isCurrent
                  ? 'bg-blue-50 border border-blue-200'
                  : 'border border-gray-200'
              }`}
            >
              <div className="text-sm font-medium mb-1">
                Week {week.weekNumber}
              </div>
              <div className="text-xs text-gray-500 mb-2">
                {week.displayDates}
              </div>
              <input
                type="number"
                className="w-full p-2 border rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={displayValue}
                defaultValue={displayValue ?? '0'}
                onChange={(e) => {
                  handleHourChange(
                    week,
                    hourEntry?.hours_worked,
                    e.target.value,
                  );
                }}
                min="0"
                max="168"
                step="0.25"
              />
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          onClick={() => handleSaveAll(project.project_id)}
          disabled={isLoading || !isPendingChanges || !isValidPendingChanges}
        >
          <Save className="w-4 h-4" />
          {isLoading ? 'Saving...' : 'Save Hours'}
        </Button>
      </div>
    </div>
  );
}
